import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import type { TicketSummaryPage } from '../api/easeedesk'
import { imageAnalysisQuery } from '../api/easeedeskTicketTabs'
import { Spinner } from '../ui/Spinner'

/**
 * Cele sapte blocuri ale coloanei din stanga a rezumatului care vin dupa
 * "User Details" si "Current Request" - `tickets/details/summary.php` si
 * `summary/{ticket_comments,ticket_attachments,ticket_statuses}.php`, in
 * ordinea de acolo, cu markupul de acolo: fiecare e un `<h4>` cu sageata
 * `.toggle-details`, `hr.dotted`, si `.slide-details` cu un `table.details-table`.
 *
 * Ce era gol in macheta nu se deseneaza nici aici: comentariile, imaginile,
 * fisierele, abonatii, statusurile, campurile proprii si feedbackul apar numai
 * cand au ceva; "Times of Operations" e mereu, cu randurile lui goale sarite.
 */

/** `convert_bytes()` din helperul portalului. */
export function bytes(size: number): string {
  if (size < 1024) return `${size} B`
  const units = ['KB', 'MB', 'GB']
  let value = size / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(2)} ${units[unit]}`
}

/** Un bloc care se strange: `<h4><i class="toggle-details">` + `hr.dotted` + `.slide-details`. */
export function Section({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(true)

  return (
    <div>
      <h4>
        <i className={`fa ${open ? 'fa-angle-up' : 'fa-angle-down'} toggle-details`} aria-hidden="true" onClick={() => setOpen(!open)}></i> {title}
      </h4>
      <hr className="dotted" style={{ marginTop: 5 }} />
      <div className="slide-details" hidden={!open}>
        {children}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/*  LAST UPDATES - `summary/ticket_comments.php`                              */
/* ------------------------------------------------------------------------- */

/** Cate litere din comentariu se arata inainte de "... more" (`lastUpdateComment($c, 250)`). */
const STRIPPED = 250

export function LastUpdates({ comments }: { comments: TicketSummaryPage['comments'] }) {
  if (comments.length === 0) return null

  return (
    <Section title="Last Updates">
      <ul>
        {comments.map((comment, i) => (
          <li key={i} style={{ marginBottom: 20 }}>
            <div className="color-green-meadow">
              <i className="fas fa-comment"></i> <strong>{comment.name}</strong> on <em>{comment.created}</em>
            </div>
            <div style={{ wordWrap: 'break-word', display: 'inline-block' }}>
              <Update text={comment.comment} />
            </div>
          </li>
        ))}
      </ul>
    </Section>
  )
}

/**
 * Textul taiat la 250, cu "... more" care il desface - `ticket_comments-1.js`.
 * Textul e cel cu `<br>` facut rand nou (`br2nl`), deci se scrie ca TEXT, cu
 * randurile pastrate; portalul il scria brut in pagina.
 */
function Update({ text }: { text: string }) {
  const [full, setFull] = useState(false)

  if (text.length <= STRIPPED) return <span style={{ whiteSpace: 'pre-line' }}>{text}</span>

  return (
    <>
      {!full && <div className="stripped-update" style={{ whiteSpace: 'pre-line' }}>{text.slice(0, STRIPPED)}</div>}
      {full && <div className="full-update" style={{ whiteSpace: 'pre-line' }}>{text}</div>}
      {!full && (
        <span className="show-full-update" style={{ cursor: 'pointer', color: '#0AA699' }} onClick={() => setFull(true)}>
          ... more
        </span>
      )}
    </>
  )
}

/* ------------------------------------------------------------------------- */
/*  IMAGES si ATTACHMENTS - `summary/ticket_attachments.php`                  */
/* ------------------------------------------------------------------------- */

export function Images({ ticket, images }: { ticket: number; images: TicketSummaryPage['images'] }) {
  if (images.length === 0) return null

  return (
    <Section title="Images">
      <ul className="attachments-list row">
        {images.map((image) => (
          <li key={image.id} className="attachment-image-preview-li col-md-6" style={{ width: '45%', verticalAlign: 'top' }}>
            <div className="attachment-image-thumb">
              <a href={image.url} target="_blank" rel="noreferrer" data-title={image.name}>
                <img src={image.thumbnail !== '' ? image.thumbnail : image.url} alt="" />
              </a>
            </div>
            <VisionAnalysis ticket={ticket} image={image.id} />
            <div className="attachment-image-details">
              <div className="attachment-image-title">
                <a href={image.download}>
                  <i className="fas fa-cloud-download-alt" aria-hidden="true"></i> {image.name}
                </a>
              </div>
              <div className="attachment-image-size">{bytes(image.size)}</div>
            </div>
          </li>
        ))}
      </ul>
    </Section>
  )
}

/** Cate etichete si cate texte se arata inainte de "show more" (`i <= 2`). */
const VISION_SHOWN = 3

/**
 * "AI Vision Analysis" sub fiecare poza - `.image-analizer-summary` din
 * `ticket_attachments.php`, umplut de `ticket_attachments-1.js` cu jQuery
 * (`$.get(analizeImage)`, `slideToggle` pe "show more"). Cand scriptul a iesit,
 * blocul a disparut cu el; aici se cere din React, cu ACEEASI cerere ca fila
 * Manuals (`imageAnalysisQuery`, aceeasi cheie) - deci o poza se analizeaza o
 * singura data, oricare din cele doua s-ar deschide intai.
 *
 * Cine n-are dreptul filei Manuals primeste 403 de la server: blocul nu se
 * deseneaza deloc, in loc sa spuna "Something went wrong" pe fiecare poza.
 */
function VisionAnalysis({ ticket, image }: { ticket: number; image: number }) {
  const analysis = useQuery(imageAnalysisQuery(ticket, image))
  const [moreLabels, setMoreLabels] = useState(false)
  const [moreText, setMoreText] = useState(false)

  if (analysis.isError) return null

  const ok = analysis.data?._meta?.status === 'OK'
  const labels = ok ? (analysis.data?.data?.labels ?? []) : []
  const texts = ok ? (analysis.data?.data?.text ?? []) : []

  return (
    <div className="image-analizer-summary" style={{ padding: 10 }}>
      <h5 className="bordered">AI Vision Analysis</h5>
      <div className="analizer-results">
        {analysis.isPending ? (
          <Spinner />
        ) : !ok ? (
          <div className="alert alert-danger">Something went wrong. Please try again later.</div>
        ) : (
          <>
            {labels.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                {(moreLabels ? labels : labels.slice(0, VISION_SHOWN)).map((label, i) => (
                  <span key={i} className="label label-primary" style={{ marginRight: 4 }}>
                    {label}
                  </span>
                ))}
                {labels.length > VISION_SHOWN && <More open={moreLabels} onToggle={() => setMoreLabels(!moreLabels)} />}
              </div>
            )}
            {texts.length > 0 && (
              <div>
                {(moreText ? texts : texts.slice(0, VISION_SHOWN)).map((text, i) => (
                  <span key={i} className="label label-success" style={{ marginRight: 4, whiteSpace: 'pre-wrap' }}>
                    {text}
                  </span>
                ))}
                {texts.length > VISION_SHOWN && <More open={moreText} onToggle={() => setMoreText(!moreText)} />}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function More({ open, onToggle }: { open: boolean; onToggle(): void }) {
  return (
    <span className="toggle-more" style={{ display: 'block', cursor: 'pointer', marginBottom: 5 }} onClick={onToggle}>
      <i className={`fa ${open ? 'fa-angle-up' : 'fa-angle-down'}`} aria-hidden="true"></i> {open ? 'show less' : 'show more'}
    </span>
  )
}

export function Attachments({ files }: { files: TicketSummaryPage['files'] }) {
  if (files.length === 0) return null

  return (
    <Section title="Attachments">
      <ul className="attachments-list">
        {files.map((file) => (
          <li key={file.id} className="attachment-file-preview">
            <a href={file.download}>
              <i className="fa fa-file" aria-hidden="true"></i> {file.name} ({bytes(file.size)})
            </a>
          </li>
        ))}
      </ul>
    </Section>
  )
}

/* ------------------------------------------------------------------------- */
/*  SUBSCRIBERS                                                               */
/* ------------------------------------------------------------------------- */

export function Subscribers({ names }: { names: string[] }) {
  if (names.length === 0) return null

  return (
    <Section title="Subscribers">
      <table className="details-table" width="100%">
        <tbody>
          <tr>
            <td>
              {names.map((name, i) => (
                <span className="label label-default" key={i}>
                  <i className="far fa-user" aria-hidden="true"></i> {name}
                </span>
              ))}
            </td>
          </tr>
        </tbody>
      </table>
    </Section>
  )
}

/* ------------------------------------------------------------------------- */
/*  OPERATIONS SUMMARY - `summary/ticket_statuses.php` + `operationsSummary()` */
/* ------------------------------------------------------------------------- */

export function Operations({ operations }: { operations: TicketSummaryPage['operations'] }) {
  if (operations.rows.length === 0) return null

  return (
    <Section title="Operations Summary">
      <table className="details-table" width="100%">
        <tbody>
          {operations.sla && (
            <tr>
              <td width="1%" className="title" style={{ verticalAlign: 'middle' }}>
                <b>Ticket Type Resolution SLA</b>
              </td>
              <td>
                <span>{operations.sla.label}</span>
                <div className="progress" style={{ height: 12, marginBottom: 5 }}>
                  <div className={`progress-bar progress-bar-striped ${operations.sla.tone} active`} role="progressbar" style={{ width: `${operations.sla.percent}%` }}></div>
                </div>
              </td>
            </tr>
          )}
          {operations.rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px dashed #ddd' }}>
              <td className="title" style={{ verticalAlign: 'middle' }}>
                {row.name}
              </td>
              <td>
                {row.started}
                {row.percent !== null ? (
                  <>
                    <div className="progress" style={{ height: 8, marginTop: 5, marginBottom: 0 }}>
                      <div className={`progress-bar progress-bar-striped ${row.percent < 100 ? 'progress-bar-success' : 'progress-bar-danger'}`} role="progressbar" style={{ width: `${row.percent}%` }}></div>
                    </div>
                    <span className={row.tone}>{row.message}</span>
                    <span className="pull-right" style={{ color: '#bbb' }}>
                      {row.firstStep && 'Current step SLA '}
                      <i className="far fa-clock" aria-hidden="true"></i>
                      {row.estimated}
                    </span>
                  </>
                ) : (
                  <p>{row.message}</p>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  )
}

/* ------------------------------------------------------------------------- */
/*  TIMES OF OPERATIONS                                                       */
/* ------------------------------------------------------------------------- */

export function Times({ times }: { times: TicketSummaryPage['times'] }) {
  return (
    <Section title="Times of Operations">
      <table className="details-table" width="100%">
        <tbody>
          {times.deliveryTime !== '' && (
            <tr>
              <td className="title color-primary">
                <strong>Delivery time</strong>
              </td>
              <td className="color-primary">
                <strong>{times.deliveryTime}</strong>
              </td>
            </tr>
          )}
          <tr>
            <td className="title">Created</td>
            <td>{times.created}</td>
          </tr>
          {times.updated !== '' && (
            <tr>
              <td className="title">Updated</td>
              <td>{times.updated}</td>
            </tr>
          )}
          {times.duedate !== '' && (
            <tr>
              <td className="title">Duedate</td>
              <td>{times.duedate}</td>
            </tr>
          )}
          {times.estimatedEffort !== '' && (
            <tr>
              <td className="title">Estimated effort</td>
              <td>{times.estimatedEffort}</td>
            </tr>
          )}
          {times.estimatedIntervention !== '' && (
            <tr>
              <td className="title">Estimated time of intervention</td>
              <td>{times.estimatedIntervention}</td>
            </tr>
          )}
          {times.resolved !== '' && (
            <tr>
              <td className="title color-green">
                <i className="fas fa-check-circle" aria-hidden="true"></i> Resolved
              </td>
              <td className="color-green">{times.resolved}</td>
            </tr>
          )}
        </tbody>
      </table>
    </Section>
  )
}

/* ------------------------------------------------------------------------- */
/*  CUSTOM FIELDS                                                             */
/* ------------------------------------------------------------------------- */

export function CustomFields({ fields }: { fields: TicketSummaryPage['customFields'] }) {
  if (fields.length === 0) return null

  return (
    <Section title="Custom fields">
      <table className="details-table" width="100%">
        <tbody>
          {fields.map((field, i) => (
            <tr key={i}>
              <td className="title">{field.label}</td>
              <td>
                <ul>
                  {field.values.map((value, j) => (
                    <li key={j}>{value}</li>
                  ))}
                </ul>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  )
}

/* ------------------------------------------------------------------------- */
/*  FEEDBACK                                                                  */
/* ------------------------------------------------------------------------- */

export function Feedback({ feedback }: { feedback: TicketSummaryPage['feedback'] }) {
  if (feedback === null) return null
  const tone = feedback.rating === 10 ? 'color-green' : 'color-red'

  return (
    <Section title="Feedback">
      <table className="details-table" width="100%">
        <tbody>
          <tr>
            <td className={`title ${tone}`}>Rating</td>
            <td className={tone}>{feedback.rating}</td>
          </tr>
          {feedback.comments !== '' && (
            <tr>
              <td className="title">Suggestions</td>
              <td>
                <em>{feedback.comments}</em>
              </td>
            </tr>
          )}
          <tr>
            <td className="title">Sender</td>
            <td>{feedback.sender}</td>
          </tr>
          <tr>
            <td className="title">Date</td>
            <td>{feedback.created}</td>
          </tr>
        </tbody>
      </table>
    </Section>
  )
}
